import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Divider,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { WarningAmber as WarningIcon, AccountBalance as BankIcon, AttachFile as AttachIcon, Block as ExcludeIcon, RestartAlt as RestoreIcon, Add as AddIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import config from '../config';

interface BankStatementTransaction {
  id: string;
  date: string;
  value_date: string | null;
  description: string | null;
  counterparty: string | null;
  debit_amount: string;
  credit_amount: string;
  balance_after: string;
  direction: 'money_in' | 'money_out';
  payment_method: string | null;
  category_hint: string | null;
  /** User-assigned free-text label (e.g. "Rent"), edited inline from the Tag column — mirrors
   * BankTransaction.tag/BankConnectionDetailDialog's own Tag column, for column parity between
   * the live-feed and statement transaction views. */
  tag?: string | null;
  excluded?: boolean;
  linked_proofs?: any[];
}

interface BankStatementRecord {
  id: string;
  bank_name: string | null;
  account_holder_name: string | null;
  account_number: string | null;
  iban: string | null;
  currency: string;
  statement_date: string | null;
  period_start_date: string | null;
  period_end_date: string | null;
  opening_balance: string | null;
  closing_balance: string | null;
  total_debits: string | null;
  total_credits: string | null;
  debits_mismatch: boolean;
  credits_mismatch: boolean;
  balance_mismatch: boolean;
  original_filename: string | null;
  created_at: string;
  transaction_count: number;
}

interface BankStatementDetailDialogProps {
  open: boolean;
  onClose: () => void;
  statement: BankStatementRecord | null;
  transactions: BankStatementTransaction[] | undefined;
  loading: boolean;
  onTransactionUpdated: (updated: BankStatementTransaction) => void;
  /** A manual, document-less transaction was added directly onto this statement (see the
   * "Add transaction" button in the header) — mirrors BankConnectionDetailDialog's
   * onAddNewTransaction, except this dialog owns the whole add flow itself (a statement
   * transaction always belongs to this one statement, so there's no document-picker step). */
  onTransactionCreated: (created: BankStatementTransaction) => void;
  onUploadProof: (bankStatementTransactionId: string) => void;
  onViewProof: (proof: any) => void;
}

// A "rebuilt" digital replica of the uploaded statement PDF, not just a bare transaction list —
// same header fields (bank/account holder/account number/IBAN), same running-balance table shape
// (opening balance -> every line -> totals -> closing balance) the original document itself has,
// so the user can explore the whole document without reopening the PDF.
const fmtAmount = (val: string | number | null | undefined, currency: string) => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: currency || 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(val || 0));
  } catch {
    return `${Number(val || 0).toFixed(2)} ${currency || ''}`.trim();
  }
};

type EditField = 'counterparty' | 'debit_amount' | 'credit_amount';

const BankStatementDetailDialog: React.FC<BankStatementDetailDialogProps> = ({ open, onClose, statement, transactions, loading, onTransactionUpdated, onTransactionCreated, onUploadProof, onViewProof }) => {
  const theme = useTheme();
  const { palette } = theme;
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Local editable copy — OCR on a real (often bilingual/scanned) statement can misread a name
  // or a digit, so every counterparty/debit/credit cell below is click-to-edit, matching the
  // same pattern ReceiptCard.tsx already uses for receipt line items. Kept in local state (synced
  // from props) so an edit updates the on-screen sums immediately, not just after a refetch.
  const [localTxns, setLocalTxns] = useState<BankStatementTransaction[]>(transactions || []);
  useEffect(() => { setLocalTxns(transactions || []); }, [transactions]);

  const [editingField, setEditingField] = useState<{ id: string; field: EditField } | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');

  // Inline tag editing (see BankStatementTransaction.tag) — same Autocomplete-chip pattern as
  // BankConnectionDetailDialog's own Tag column, including the "already-used tags" dropdown.
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState('');
  const existingTags = Array.from(new Set(localTxns.map(t => (t.tag || '').trim()).filter(Boolean))).sort();
  const commitTag = async (t: BankStatementTransaction, value: string) => {
    setEditingTagId(null);
    const newTag = value.trim();
    const oldTag = (t.tag || '').trim();
    if (newTag === oldTag) return;
    setLocalTxns(prev => prev.map(x => (x.id === t.id ? { ...x, tag: newTag || null } : x)));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/bank-statement-transactions/${t.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: newTag }),
      });
      if (!res.ok) throw new Error('update failed');
      const data = await res.json();
      setLocalTxns(prev => prev.map(x => (x.id === t.id ? data.transaction : x)));
      onTransactionUpdated(data.transaction);
    } catch (error) {
      setLocalTxns(prev => prev.map(x => (x.id === t.id ? t : x))); // revert on failure
    }
  };

  // "Add transaction" — a manual, document-less row added directly onto this statement (see
  // POST /api/bank-statements/<id>/transactions), the statements-view equivalent of
  // BankConnectionDetailDialog's "Add new transaction" blank-manual-entry mode.
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addDescription, setAddDescription] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [addDirection, setAddDirection] = useState<'money_out' | 'money_in'>('money_out');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const openAddDialog = () => {
    setAddDate(new Date().toISOString().slice(0, 10));
    setAddDescription('');
    setAddAmount('');
    setAddDirection('money_out');
    setAddError(null);
    setAddDialogOpen(true);
  };

  const submitAddTransaction = async () => {
    if (!statement) return;
    const parsedAmount = parseFloat(addAmount);
    if (!addDate) { setAddError('Date is required'); return; }
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) { setAddError('Enter a valid amount'); return; }
    setAddSaving(true);
    setAddError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/bank-statements/${statement.id}/transactions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: addDate, description: addDescription, total: parsedAmount.toFixed(2), direction: addDirection }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add transaction');
      setLocalTxns(prev => [...prev, data.transaction]);
      onTransactionCreated(data.transaction);
      setAddDialogOpen(false);
    } catch (error: any) {
      setAddError(error.message || 'Failed to add transaction');
    } finally {
      setAddSaving(false);
    }
  };

  const [mismatchDismissed, setMismatchDismissed] = useState(false);
  useEffect(() => { setMismatchDismissed(false); }, [statement?.id]);

  if (!statement) return null;

  const currency = statement.currency || 'GBP';

  const startEditField = (t: BankStatementTransaction, field: EditField) => {
    setEditingField({ id: t.id, field });
    setEditFieldValue(field === 'counterparty' ? (t.counterparty || t.description || '') : Number(t[field] || 0).toFixed(2));
  };

  const cancelEditField = () => setEditingField(null);

  const commitEditField = async () => {
    if (!editingField) return;
    const { id, field } = editingField;
    const original = localTxns.find(t => t.id === id);
    const trimmed = editFieldValue.trim();
    setEditingField(null);
    if (!original || !trimmed) return; // OCR-wrong is fine, blank is not — just drop the edit

    if (field !== 'counterparty' && isNaN(parseFloat(trimmed))) return;
    const unchanged = field === 'counterparty' ? trimmed === (original.counterparty || original.description || '') : trimmed === Number(original[field] || 0).toFixed(2);
    if (unchanged) return;

    setLocalTxns(prev => prev.map(t => (t.id === id ? { ...t, [field]: trimmed } : t)));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/bank-statement-transactions/${id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: trimmed }),
      });
      if (!res.ok) throw new Error('update failed');
      const data = await res.json();
      setLocalTxns(prev => prev.map(t => (t.id === id ? data.transaction : t)));
      onTransactionUpdated(data.transaction);
    } catch (error) {
      setLocalTxns(prev => prev.map(t => (t.id === id ? original : t))); // revert on failure
    }
  };

  // "Not relevant to my business account" (e.g. a transfer to personal savings) — persisted,
  // reversible; the row is never deleted, just excluded from the sums above and every
  // Income/Expenses figure elsewhere until toggled back.
  const toggleExcluded = async (t: BankStatementTransaction) => {
    const nextExcluded = !t.excluded;
    setLocalTxns(prev => prev.map(x => (x.id === t.id ? { ...x, excluded: nextExcluded } : x)));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/bank-statement-transactions/${t.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ excluded: nextExcluded }),
      });
      if (!res.ok) throw new Error('update failed');
      const data = await res.json();
      setLocalTxns(prev => prev.map(x => (x.id === t.id ? data.transaction : x)));
      onTransactionUpdated(data.transaction);
    } catch (error) {
      setLocalTxns(prev => prev.map(x => (x.id === t.id ? t : x))); // revert on failure
    }
  };

  // The statement's own printed totals are authoritative (same "extracted total is authoritative,
  // mismatch is flagged not silently corrected" principle used for receipts/invoices) — recomputed
  // from localTxns (not the original props) so correcting a misread amount above updates this
  // comparison immediately, letting the user see for themselves whether it now reconciles.
  const summedDebits = localTxns.reduce((s, t) => s + Number(t.debit_amount || 0), 0);
  const summedCredits = localTxns.reduce((s, t) => s + Number(t.credit_amount || 0), 0);
  const debitsMismatch = Math.abs(summedDebits - Number(statement.total_debits || 0)) > 0.01;
  const creditsMismatch = Math.abs(summedCredits - Number(statement.total_credits || 0)) > 0.01;
  const hasMismatch = debitsMismatch || creditsMismatch || statement.balance_mismatch;

  // VAT for the whole statement — summed from whichever invoice(s)/receipts are attached as
  // proof of each transaction, split by that transaction's own direction (same derivation as
  // the table's own VAT (IN)/VAT (OUT) columns below).
  const vatOut = localTxns.filter(t => t.direction !== 'money_in').reduce((s, t) => s + (t.linked_proofs || []).reduce((ps, p) => ps + Number(p.tax || 0), 0), 0);
  const vatIn = localTxns.filter(t => t.direction === 'money_in').reduce((s, t) => s + (t.linked_proofs || []).reduce((ps, p) => ps + Number(p.tax || 0), 0), 0);

  const openingBalance = Number(statement.opening_balance || 0);
  const closingBalance = Number(statement.closing_balance || 0);
  // No meaningful percentage against a zero (or missing) opening balance — any change from £0
  // is an undefined/infinite percentage, not a real growth rate.
  const balancePctChange = openingBalance !== 0 ? ((closingBalance - openingBalance) / Math.abs(openingBalance)) * 100 : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} sx={{ '& .MuiDialog-paper': { maxWidth: 945 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BankIcon sx={{ color: palette.tokens.muted }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {statement.bank_name || 'Bank statement'}
            </Typography>
            <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
              {statement.account_holder_name}
            </Typography>
          </Box>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}>
            Add new transaction
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* ── statement header, replicating the printed document's own summary block ── */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2,
              p: 2, mb: 2.5, bgcolor: palette.tokens.surfaceSoft, borderRadius: 2,
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Account number</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{statement.account_number || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>IBAN</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink, wordBreak: 'break-all' }}>{statement.iban || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Currency</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{currency}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Statement date</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{statement.statement_date || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Period</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>
                  {statement.period_start_date && statement.period_end_date ? `${statement.period_start_date} – ${statement.period_end_date}` : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Transactions</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{localTxns.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Opening balance</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>{fmtAmount(statement.opening_balance, currency)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Closing balance</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink }}>{fmtAmount(statement.closing_balance, currency)}</Typography>
                  {balancePctChange !== null && (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: balancePctChange >= 0 ? palette.tokens.success : palette.tokens.error }}>
                      {balancePctChange >= 0 ? '▲' : '▼'} {Math.abs(balancePctChange).toFixed(1)}%
                    </Typography>
                  )}
                </Box>
              </Box>
              {(vatOut > 0 || vatIn > 0) && (
                <Box>
                  <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>VAT</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>
                    {vatOut > 0 && `Out ${fmtAmount(vatOut, currency)}`}
                    {vatOut > 0 && vatIn > 0 ? ' · ' : ''}
                    {vatIn > 0 && `In ${fmtAmount(vatIn, currency)}`}
                  </Typography>
                </Box>
              )}
            </Box>

            {hasMismatch && !mismatchDismissed && (
              <Alert severity="warning" icon={<WarningIcon />} onClose={() => setMismatchDismissed(true)} sx={{ mb: 2 }}>
                This statement's own printed totals don't fully reconcile with what was extracted per-transaction — likely an OCR misread somewhere below. Click any name or amount to correct it; the "Summed from transactions" row updates live.
              </Alert>
            )}

            {/* ── running-balance ledger, same shape as the original document's own table ── */}
            <Box sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, overflow: 'auto', maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Credit<Box component="span" sx={{ display: 'block', fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(IN)</Box></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Debit<Box component="span" sx={{ display: 'block', fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(OUT)</Box></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>VAT<Box component="span" sx={{ display: 'block', fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(IN)</Box></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>VAT<Box component="span" sx={{ display: 'block', fontSize: 9, fontWeight: 500, textTransform: 'none' }}>(OUT)</Box></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Balance</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft }}>Proof</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, width: 90, maxWidth: 90 }}>Tag</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: palette.tokens.mutedSoft, width: 36, pl: 0.5, pr: 1 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ bgcolor: palette.tokens.surfaceSoft }}>
                    <TableCell sx={{ fontSize: 12.5, color: palette.tokens.mutedSoft }}>{statement.period_start_date || statement.statement_date || ''}</TableCell>
                    <TableCell colSpan={2} sx={{ fontSize: 12.5, fontStyle: 'italic', color: palette.tokens.mutedSoft }}>Balance brought forward</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, color: palette.tokens.ink }}>{fmtAmount(statement.opening_balance, currency)}</TableCell>
                    <TableCell />
                  </TableRow>

                  {localTxns.map(t => {
                    const editingCounterparty = editingField?.id === t.id && editingField.field === 'counterparty';
                    const editingDebit = editingField?.id === t.id && editingField.field === 'debit_amount';
                    const editingCredit = editingField?.id === t.id && editingField.field === 'credit_amount';
                    // VAT for the row — summed from whichever invoice(s) are attached as proof of
                    // it. Never stored independently, so it always matches the invoice/receipt.
                    const vatAmount = (t.linked_proofs || []).reduce((sum, p) => sum + Number(p.tax || 0), 0);
                    return (
                      <TableRow key={t.id} hover sx={t.excluded ? { opacity: 0.45 } : undefined}>
                        <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{t.date}</TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {editingCounterparty ? (
                            <TextField
                              size="small" autoFocus fullWidth
                              value={editFieldValue}
                              onChange={(e) => setEditFieldValue(e.target.value)}
                              onBlur={commitEditField}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitEditField(); if (e.key === 'Escape') cancelEditField(); }}
                            />
                          ) : (
                            <Tooltip title="Click to edit">
                              <Typography
                                variant="body2"
                                onClick={() => startEditField(t, 'counterparty')}
                                sx={{ fontWeight: 600, fontSize: 12.5, color: palette.tokens.ink, cursor: 'pointer', '&:hover': { color: palette.tokens.primary, textDecoration: 'underline' } }}
                              >
                                {t.counterparty || t.description || 'Transaction'}
                              </Typography>
                            </Tooltip>
                          )}
                          {t.description && t.counterparty && t.description !== t.counterparty && (
                            <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, display: 'block' }}>{t.description}</Typography>
                          )}
                          {(t.category_hint || t.payment_method) && (
                            <Typography variant="caption" sx={{ color: palette.tokens.hairline, display: 'block' }}>
                              {[t.category_hint, t.payment_method].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.success }}>
                          {editingCredit ? (
                            <TextField
                              size="small" autoFocus type="number"
                              value={editFieldValue}
                              onChange={(e) => setEditFieldValue(e.target.value)}
                              onBlur={commitEditField}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitEditField(); if (e.key === 'Escape') cancelEditField(); }}
                              sx={{ width: 110 }}
                            />
                          ) : (
                            <Tooltip title="Click to edit">
                              <Box component="span" onClick={() => startEditField(t, 'credit_amount')} sx={{ cursor: 'pointer', '&:hover': { color: palette.tokens.primary, textDecoration: 'underline' } }}>
                                {Number(t.credit_amount) > 0 ? fmtAmount(t.credit_amount, currency) : '—'}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.ink }}>
                          {editingDebit ? (
                            <TextField
                              size="small" autoFocus type="number"
                              value={editFieldValue}
                              onChange={(e) => setEditFieldValue(e.target.value)}
                              onBlur={commitEditField}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitEditField(); if (e.key === 'Escape') cancelEditField(); }}
                              sx={{ width: 110 }}
                            />
                          ) : (
                            <Tooltip title="Click to edit">
                              <Box component="span" onClick={() => startEditField(t, 'debit_amount')} sx={{ cursor: 'pointer', '&:hover': { color: palette.tokens.primary, textDecoration: 'underline' } }}>
                                {Number(t.debit_amount) > 0 ? fmtAmount(t.debit_amount, currency) : '—'}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.mutedSoft }}>
                          {t.direction === 'money_in' && vatAmount > 0 ? fmtAmount(vatAmount, currency) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, color: palette.tokens.mutedSoft }}>
                          {t.direction !== 'money_in' && vatAmount > 0 ? fmtAmount(vatAmount, currency) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 600, color: palette.tokens.ink, whiteSpace: 'nowrap' }}>
                          {fmtAmount(t.balance_after, currency)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {(t.linked_proofs || []).map((p) => (
                              <Chip
                                key={p.id}
                                size="small"
                                label={p.contractor_name || (p.document_type === 'Receipt' ? 'Receipt' : 'Invoice')}
                                onClick={() => onViewProof(p)}
                                sx={{ fontSize: 10, height: 20, cursor: 'pointer', bgcolor: alpha(palette.tokens.primary, 0.1), color: palette.tokens.primary, fontWeight: 700 }}
                              />
                            ))}
                            <Chip
                              size="small"
                              icon={<AttachIcon sx={{ fontSize: '13px !important' }} />}
                              label={(t.linked_proofs || []).length > 0 ? 'Add' : 'Upload proof'}
                              onClick={() => onUploadProof(t.id)}
                              variant="outlined"
                              sx={{ fontSize: 10, height: 20, cursor: 'pointer', borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11.5, width: 90, maxWidth: 90, overflow: 'hidden' }}>
                          {editingTagId === t.id ? (
                            <Autocomplete
                              freeSolo
                              disableClearable
                              forcePopupIcon={false}
                              size="small"
                              options={existingTags}
                              inputValue={tagDraft}
                              onInputChange={(_e, val) => setTagDraft(val)}
                              onChange={(_e, val) => commitTag(t, val || '')}
                              onBlur={() => commitTag(t, tagDraft)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); commitTag(t, tagDraft); }
                                if (e.key === 'Escape') setEditingTagId(null);
                              }}
                              renderInput={(params) => (
                                <TextField {...params} autoFocus variant="standard" placeholder="Tag…" sx={{ '& .MuiInputBase-input': { fontSize: 11.5 } }} />
                              )}
                              slotProps={{ popper: { style: { width: 180 } } }}
                              sx={{ width: 90, maxWidth: 90 }}
                            />
                          ) : t.tag ? (
                            <Chip
                              size="small"
                              label={t.tag}
                              onClick={() => { setTagDraft(t.tag || ''); setEditingTagId(t.id); }}
                              onDelete={() => commitTag(t, '')}
                              sx={{ fontSize: 10, height: 20, maxWidth: 88, cursor: 'pointer', bgcolor: alpha(palette.tokens.primary, 0.1), color: palette.tokens.primary, fontWeight: 700 }}
                            />
                          ) : (
                            <Box
                              component="span"
                              onClick={() => { setTagDraft(''); setEditingTagId(t.id); }}
                              sx={{ cursor: 'pointer', fontSize: 11, color: palette.tokens.hairline, whiteSpace: 'nowrap', '&:hover': { color: palette.tokens.muted } }}
                            >
                              + Add
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ width: 36, pl: 0.5, pr: 1 }}>
                          <Tooltip title={t.excluded ? 'Not counted as a business transaction — click to restore' : "Mark as not relevant to my business account"}>
                            <IconButton size="small" onClick={() => toggleExcluded(t)}>
                              {t.excluded ? <RestoreIcon sx={{ fontSize: 16, color: palette.tokens.mutedSoft }} /> : <ExcludeIcon sx={{ fontSize: 15, color: palette.tokens.hairline }} />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  <TableRow sx={{ '& td': { borderTop: `2px solid ${palette.tokens.hairline}` } }}>
                    <TableCell colSpan={2} sx={{ fontSize: 12.5, fontWeight: 700, color: palette.tokens.ink }}>Totals (printed on statement)</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, color: palette.tokens.ink }}>
                      {fmtAmount(statement.total_credits, currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, color: palette.tokens.ink }}>
                      {fmtAmount(statement.total_debits, currency)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>

                  {/* Always shown, not just on mismatch — so the user can see for themselves
                      whether the two rows agree, and watch this one update live as they edit. */}
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontSize: 12, fontStyle: 'italic', color: palette.tokens.mutedSoft }}>Summed from transactions</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600, color: creditsMismatch ? '#d97706' : palette.tokens.mutedSoft }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {creditsMismatch && <WarningIcon sx={{ fontSize: 13 }} />}
                        {fmtAmount(summedCredits, currency)}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600, color: debitsMismatch ? '#d97706' : palette.tokens.mutedSoft }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {debitsMismatch && <WarningIcon sx={{ fontSize: 13 }} />}
                        {fmtAmount(summedDebits, currency)}
                      </Box>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>

                  <TableRow sx={{ bgcolor: palette.tokens.surfaceSoft }}>
                    <TableCell sx={{ fontSize: 12.5, color: palette.tokens.mutedSoft }}>{statement.period_end_date || statement.statement_date || ''}</TableCell>
                    <TableCell colSpan={2} sx={{ fontSize: 12.5, fontStyle: 'italic', color: palette.tokens.mutedSoft }}>
                      Balance carried forward
                      {statement.balance_mismatch && (
                        <Tooltip title="Doesn't reconcile with opening balance + totals above">
                          <WarningIcon sx={{ fontSize: 14, color: '#d97706', ml: 0.5, verticalAlign: 'text-bottom' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 800, color: palette.tokens.ink }}>
                      {fmtAmount(statement.closing_balance, currency)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
              {statement.original_filename} · uploaded {new Date(statement.created_at).toLocaleString('en-GB')}
              {statement.transaction_count === 0 && ' · no transactions extracted'}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* "Add new transaction" — a manual, document-less row added directly onto this statement
          (see POST /api/bank-statements/<id>/transactions). Deliberately doesn't touch the
          statement's own printed totals (opening/closing/total_debits/total_credits) — those
          stay exactly what the PDF says, same principle as an OCR correction above. */}
      <Dialog open={addDialogOpen} onClose={() => (addSaving ? null : setAddDialogOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add a new transaction</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {addError && <Alert severity="error" sx={{ fontSize: 13 }}>{addError}</Alert>}
          <TextField
            label="Date" type="date" size="small" fullWidth
            value={addDate}
            onChange={(e) => setAddDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Description" size="small" fullWidth
            value={addDescription}
            onChange={(e) => setAddDescription(e.target.value)}
            placeholder="e.g. Rent payment"
          />
          <TextField
            label="Amount" type="number" size="small" fullWidth
            value={addAmount}
            onChange={(e) => setAddAmount(e.target.value)}
          />
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={addDirection}
            onChange={(_e, val) => val && setAddDirection(val)}
          >
            <ToggleButton value="money_out">Money out</ToggleButton>
            <ToggleButton value="money_in">Money in</ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addSaving} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" onClick={submitAddTransaction} disabled={addSaving} sx={{ borderRadius: 2 }}>
            {addSaving ? <CircularProgress size={18} /> : 'Add transaction'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BankStatementDetailDialog;
